import { type ChangeEvent, type FC, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Icon, Input, Table, useDebouncedCallback } from '@wf/components';

import { type ILicenseSearchResult } from '../../../Licenses/store/types';
import TableFooter from '../../../../components/TableFooter';
import TableGrid from '../../../../components/TableGrid';

import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import { Operators } from '../../../../@types/serviceTypes';
import { SortDirection } from '../../../../constants';
import { type ISortConfig } from '../../../../types';

import {
  setChildrenCurrentPage,
  setChildrenLoading,
  setChildrenSearch,
  setChildrenSortConfig,
} from '../../store/slices';
import { childrenSelector } from '../../store/selectors';
import { getChildrenTableColumns } from './tableConfigs';
import { getChildren } from '../../store/actions';
import { SearchSelectField } from '../../configs';

import styles from './LicenseChildrenTable.module.scss';

const LicenseChildrenTable: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { id } = useParams();

  const {
    data,
    isLoading,
    search: { query },
    totalCount,
    pagination: { currentPage, size },
    sortConfig,
  } = useSelector(childrenSelector);

  const licenseAbbreviation = useLicenseAbbreviation();

  useEffect(() => {
    dispatch(
      getChildren({
        page: currentPage,
        pageSize: size,
        sortByFields: {
          field: sortConfig.field,
          mode: sortConfig.mode,
        },
        selectFields: Object.keys(SearchSelectField),
        excludedStatuses: [],
        restrictions: {
          query: {
            field: 'type_number_status',
            operator: Operators.CONTAINS,
            value: query?.value ?? '',
          },
          parentLpcoId: {
            field: 'parentLpcoId',
            value: Number(id),
            operator: Operators.EQUALS,
          },
        },
      })
    );
  }, [currentPage, sortConfig, dispatch, query, id]);

  const handleSort = (sortKey: string, isAsc: boolean) => {
    const payload = {
      field: sortKey,
      mode: isAsc ? SortDirection.ASC : SortDirection.DESC,
    } as ISortConfig<ILicenseSearchResult>;
    dispatch(setChildrenSortConfig(payload));
    dispatch(setChildrenCurrentPage(1));
  };

  const handlePageChange = (page: number) => {
    dispatch(setChildrenCurrentPage(page));
  };

  const getChildrenData = useDebouncedCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement;
    dispatch(setChildrenCurrentPage(1));
    dispatch(
      setChildrenSearch({
        query: {
          field: 'type_number_status',
          operator: Operators.CONTAINS,
          value,
        },
      })
    );
  }, 700);

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    !isLoading && dispatch(setChildrenLoading(true));
    getChildrenData(e);
  };

  const handleRowClick = (row: ILicenseSearchResult) => {
    window.open(`../view/${row.id}`, '_blank');
  };

  const tableColumns = useMemo(() => getChildrenTableColumns(licenseAbbreviation, t), [licenseAbbreviation, t]);

  return (
    <div className={styles.tableContainer}>
      <Table>
        <Table.Search>
          <Input
            leftIcon={<Icon name="ic_search" />}
            onChange={handleQueryChange}
            placeholder={t('Search')}
            className={styles.input}
          />
        </Table.Search>
        <TableGrid
          isAsc={sortConfig.mode === SortDirection.ASC}
          isSortedBy={sortConfig.field}
          onRowClick={handleRowClick}
          handleSort={handleSort}
          columns={tableColumns}
          isLoading={isLoading}
          maxRows={size}
          rows={data}
        />
        <TableFooter handlePageChange={handlePageChange} pageSize={size} totalCount={totalCount} page={currentPage} />
      </Table>
    </div>
  );
};

export default LicenseChildrenTable;
